import { useEffect, useRef, useState } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { Image as ImageIcon, Loader2, Check, X, ExternalLink, Copy, RotateCcw, Upload } from 'lucide-react';
import ImageCropModal from '../../components/ImageCropModal';
import { confirm } from '../../components/ConfirmDialog';

// Superadmin → Ayarlar: giriş ekranının DEFAULT fon şəkli.
// Öz şəkli olmayan bütün tenantlarda və tokensiz /login-də görünür.
// Axın: şəkil seç → kəs (crop) → önizləmə linki → bax → Təsdiqlə (və ya Ləğv et).
const DEFAULT_BG = '/default_background.png';
const ASPECT = 16 / 10; // giriş ekranının sol paneli (geniş ekranda) təxminən bu nisbətdədir

const Thumb = ({ src, label, accent }) => (
    <div className="min-w-0 flex-1">
        <div className={`mb-1.5 text-[11px] font-semibold ${accent ? 'text-amber-700' : 'text-slate-500'}`}>{label}</div>
        <div className={`overflow-hidden rounded-xl border ${accent ? 'border-amber-300 ring-2 ring-amber-200' : 'border-slate-200'}`}
            style={{ aspectRatio: String(ASPECT) }}>
            <img src={src} alt="" className="h-full w-full object-cover" />
        </div>
    </div>
);

export default function SuperadminLoginBgCard() {
    const [state, setState] = useState({ url: null, draft_url: null, preview_path: null });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');       // 'upload' | 'approve' | 'cancel' | 'reset'
    const [cropFile, setCropFile] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get('/superadmin/login-bg')
            .then((r) => setState(r.data))
            .catch((err) => {
                // 404 / şəbəkə xətası → server bu funksiyanı hələ tanımır (backend yenilənməyib və ya işləmir)
                const st = err.response?.status;
                const msg = (!st || st === 404)
                    ? 'Fon şəkli ayarı alınmadı: server yenilənməyib və ya işləmir (backend deploy/restart lazımdır).'
                    : (err.response?.data?.msg || 'Fon şəkli ayarı alınmadı.');
                toast.error(msg, { id: 'login-bg-load', duration: 6000 });
            })
            .finally(() => setLoading(false));
    }, []);

    const previewUrl = state.preview_path ? `${window.location.origin}${state.preview_path}` : '';

    const run = async (key, fn) => {
        setBusy(key);
        try {
            const r = await fn();
            setState(r.data);
            if (r.data?.msg) toast.success(r.data.msg);
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Alınmadı.');
        } finally {
            setBusy('');
        }
    };

    const uploadDraft = (file) => run('upload', () => {
        const fd = new FormData();
        fd.append('image', file, file.name || 'login-bg.jpg');
        return api.post('/superadmin/login-bg/draft', fd);
    });

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(previewUrl); toast.success('Link kopyalandı.'); }
        catch { toast.error('Kopyalanmadı.'); }
    };

    return (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <ImageIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-900">Giriş ekranı: fon şəkli</h3>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        Öz şəkli seçilməmiş bütün restoranlarda giriş ekranının solunda görünür.
                        Şəkli seçin, kəsin, önizləmə linkində baxın, sonra təsdiqləyin.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="mt-5 flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yüklənir...</div>
            ) : (
                <>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                        <Thumb src={state.url || DEFAULT_BG} label={state.url ? 'Hazırkı şəkil' : 'Hazırkı şəkil (standart)'} />
                        {state.draft_url && <Thumb src={state.draft_url} label="Yeni şəkil (təsdiqlənməyib)" accent />}
                    </div>

                    {state.draft_url && previewUrl && (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <div className="text-[11px] font-semibold text-amber-800">Önizləmə linki</div>
                            <div className="mt-1.5 flex items-center gap-2">
                                <input readOnly value={previewUrl} onFocus={(e) => e.target.select()}
                                    className="min-w-0 flex-1 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 font-mono text-[11px] text-slate-700" />
                                <button type="button" onClick={copyLink} title="Kopyala"
                                    className="rounded-lg border border-amber-200 bg-white p-1.5 text-amber-700 hover:bg-amber-100"><Copy className="h-4 w-4" /></button>
                                <a href={previewUrl} target="_blank" rel="noreferrer"
                                    className="flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600">
                                    <ExternalLink className="h-3.5 w-3.5" /> Aç
                                </a>
                            </div>
                            <div className="mt-3 flex gap-2">
                                <button type="button" disabled={!!busy} onClick={() => run('approve', () => api.post('/superadmin/login-bg/approve'))}
                                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                                    {busy === 'approve' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Təsdiqlə
                                </button>
                                <button type="button" disabled={!!busy} onClick={() => run('cancel', () => api.delete('/superadmin/login-bg/draft'))}
                                    className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                    {busy === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Ləğv et
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setCropFile(f); }} />
                        <button type="button" disabled={!!busy} onClick={() => fileRef.current?.click()}
                            className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50">
                            {busy === 'upload' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            {state.draft_url ? 'Başqa şəkil seç' : 'Yeni şəkil seç'}
                        </button>
                        {state.url && (
                            <button type="button" disabled={!!busy}
                                onClick={async () => { if (await confirm('Standart fon şəklinə qaytarılsın?')) run('reset', () => api.post('/superadmin/login-bg/reset')); }}
                                className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                <RotateCcw className="h-4 w-4" /> Standart şəklə qaytar
                            </button>
                        )}
                    </div>
                </>
            )}

            {cropFile && (
                <ImageCropModal
                    file={cropFile}
                    aspect={ASPECT}
                    outMax={2400}
                    title="Giriş ekranının fonunu kəs"
                    onCancel={() => setCropFile(null)}
                    onConfirm={async (f) => { setCropFile(null); await uploadDraft(f); }}
                />
            )}
        </div>
    );
}
